import React, { useState } from 'react';
import { Search, Eye, Edit3, CalendarClock, XCircle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { BookingResponseDto, BookingStatus } from '../../types/booking';
import BookingStatusBadge from './BookingStatusBadge';
import SyncStatusBadge from './SyncStatusBadge';
import Spinner from '../Spinner';

const ITEMS_PER_PAGE = 10;

interface BookingTableProps {
  bookings: BookingResponseDto[];
  loading: boolean;
  timezone: string;
  onView?: (booking: BookingResponseDto) => void;
  onEdit?: (booking: BookingResponseDto) => void;
  onReschedule?: (booking: BookingResponseDto) => void;
  onCancel?: (booking: BookingResponseDto) => void;
  onComplete?: (booking: BookingResponseDto) => void;
}

export default function BookingTable({ bookings, loading, timezone, onView, onEdit, onReschedule, onCancel, onComplete }: BookingTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = bookings.filter((b) => {
    const term = searchTerm.toLowerCase();
    return (
      !term ||
      b.bookingReference.toLowerCase().includes(term) ||
      `${b.customerFirstName} ${b.customerLastName}`.toLowerCase().includes(term) ||
      b.customerEmail.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginatedData = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const canReschedule = (b: BookingResponseDto) => b.status === BookingStatus.Pending || b.status === BookingStatus.Confirmed;
  const canCancel = (b: BookingResponseDto) => b.status !== BookingStatus.Cancelled && b.status !== BookingStatus.Completed;
  const canComplete = (b: BookingResponseDto) => b.status === BookingStatus.Confirmed;

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString([], {
      timeZone: timezone,
    });

  const formatTime = (date: string) =>
    new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center bg-white p-4 rounded-lg border border-strivre-lightnavy">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-strivre-mutedtext" size={18} />
          <input
            type="text"
            placeholder="Search reference, name or email..."
            className="input-base pl-10 h-10"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-strivre-lightnavy overflow-hidden">
        {loading && (
          <div className="p-8 flex justify-center items-center">
            <Spinner size="h-8 w-8" />
            <p className="ml-3 text-strivre-mutedtext">Loading bookings...</p>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-strivre-mutedtext">No bookings found.</div>
        )}

        {/* Mobile View */}
        <div className="md:hidden divide-y divide-slate-100">
          {!loading && paginatedData.map((b) => (
            <div key={b.id} className="p-5 space-y-3">
              <div className="flex justify-between items-start gap-2">
                <div className="text-left">
                  <span className="text-[10px] font-bold text-strivre-mutedtext uppercase tracking-widest">{b.bookingReference}</span>
                  <h4 className="text-sm font-bold text-strivre-navy">{b.customerFirstName} {b.customerLastName}</h4>
                  <p className="text-xs text-strivre-mutedtext">{b.serviceName}</p>
                </div>
                <BookingStatusBadge status={b.status} />
              </div>
              <div className="flex justify-between items-center text-xs text-strivre-mutedtext">
                <span>{formatDate(b.bookingDate)} at {formatTime(b.bookingDate)}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-strivre-mutedtext">
              <div>
                <span>
                  {formatDate(b.bookingDate)} at {formatTime(b.bookingDate)}
                </span>

                <span className="ml-1 text-slate-400">
                  ({timezone})
                </span>
              </div>
            </div>
              <div className="flex gap-2">
                <button onClick={() => onView?.(b)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg" title="View"><Eye size={16} /></button>
                <button onClick={() => onEdit?.(b)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg" title="Edit"><Edit3 size={16} /></button>
                {canReschedule(b) && <button onClick={() => onReschedule?.(b)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg" title="Reschedule"><CalendarClock size={16} /></button>}
                {canComplete(b) && <button onClick={() => onComplete?.(b)} className="p-2 bg-strivre-softgrey text-emerald-600 rounded-lg" title="Complete"><CheckCircle2 size={16} /></button>}
                {canCancel(b) && <button onClick={() => onCancel?.(b)} className="p-2 bg-strivre-softgrey text-strivre-burgundy rounded-lg" title="Cancel"><XCircle size={16} /></button>}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-strivre-softgrey border-b border-strivre-lightnavy">
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Reference</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Time</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Calendar</th>
                {/* <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">CRM</th> */}
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.map((b) => (
                <tr key={b.id} className="hover:bg-strivre-softgrey/60 transition-colors">
                  <td className="px-4 py-3 font-semibold text-strivre-navy">{b.bookingReference}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-strivre-darktext">{b.customerFirstName} {b.customerLastName}</p>
                    <p className="text-xs text-strivre-mutedtext">{b.customerEmail}</p>
                  </td>
                  <td className="px-4 py-3 text-strivre-darktext">{b.serviceName}</td>
                  <td className="px-4 py-3 text-strivre-darktext">{formatDate(b.bookingDate)}</td>
                  <td className="px-4 py-3 text-strivre-darktext">
                    <div>
                      {formatTime(b.bookingDate)}

                      <div className="mt-0.5 text-[10px] text-strivre-mutedtext">
                        {timezone}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><BookingStatusBadge status={b.status} /></td>
                  <td className="px-4 py-3"><SyncStatusBadge status={b.calendarSyncStatus} /></td>
                  {/* <td className="px-4 py-3"><SyncStatusBadge status={b.crmSyncStatus} /></td> */}
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => onView?.(b)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg hover:bg-strivre-lightnavy" title="View"><Eye size={16} /></button>
                      <button onClick={() => onEdit?.(b)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg hover:bg-strivre-lightnavy" title="Edit"><Edit3 size={16} /></button>
                      {canReschedule(b) && <button onClick={() => onReschedule?.(b)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg hover:bg-strivre-lightnavy" title="Reschedule"><CalendarClock size={16} /></button>}
                      {canComplete(b) && <button onClick={() => onComplete?.(b)} className="p-2 bg-strivre-softgrey text-emerald-600 rounded-lg hover:bg-emerald-50" title="Mark Complete"><CheckCircle2 size={16} /></button>}
                      {canCancel(b) && <button onClick={() => onCancel?.(b)} className="p-2 bg-strivre-softgrey text-strivre-burgundy rounded-lg hover:bg-strivre-lightburgundy" title="Cancel"><XCircle size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-4 py-3 bg-white border-t border-strivre-lightnavy flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-strivre-mutedtext font-medium">
              Showing {paginatedData.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} bookings
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-strivre-navy hover:bg-strivre-softgrey disabled:opacity-50">
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-medium text-strivre-darktext min-w-[4.5rem] text-center">Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-strivre-navy hover:bg-strivre-softgrey disabled:opacity-50">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
